import { History } from 'lucide-react'
import { useEffect } from 'react'
import { useOutletContext } from 'react-router-dom'
import { useToast } from '../components/ToastProvider'
import { Button } from '../components/ui'
import { ToolDetailHeader } from '../features/suno/ToolDetailHeader'
import { StylePromptOverlays } from '../features/suno/StylePromptGenerator/StylePromptOverlays'
import { StylePromptWorkspace } from '../features/suno/StylePromptGenerator/StylePromptWorkspace'
import { useStylePromptGeneratorData } from '../features/suno/StylePromptGenerator/useStylePromptGeneratorData'
import type { AdminOutletContext } from '../layouts/AdminLayout'
import styles from './ToolsPage.module.css'

export const StylePromptGeneratorPage = () => {
  const { toast } = useToast()
  const { closeMobileActions, setMobileMenuItems } = useOutletContext<AdminOutletContext>()
  const {
    styleDescription,
    setStyleDescription,
    styleGenerationMode,
    setStyleGenerationMode,
    styleSuggestions,
    styleError,
    styleLoading,
    styleHistory,
    styleHistoryLoading,
    styleHistoryError,
    styleHistoryDeletingId,
    styleHistoryOpen,
    selectedBasePrompt,
    promptLibraryOpen,
    promptLibrarySearch,
    promptLibraryPrompts,
    promptLibraryLoading,
    promptLibraryError,
    sunoPromptGroups,
    promptEditor,
    promptSaving,
    styleDescriptionValid,
    styleClearAvailable,
    clearSelectedBasePrompt,
    clearSunoStylePromptWorkspace,
    deleteStyleHistoryItem,
    loadPromptLibrary,
    openGeneratedPromptSaveDialog,
    openPromptLibrary,
    openStyleHistory,
    openStyleHistoryItem,
    savePrompt,
    selectPromptLibraryPrompt,
    setPromptEditor,
    setPromptLibraryOpen,
    setPromptLibrarySearch,
    setStyleHistoryOpen,
    submitSunoStylePrompts,
    suggestPromptMetadata
  } = useStylePromptGeneratorData({ toast })

  useEffect(() => {
    setMobileMenuItems(
      <button
        className="mobile-actions-item"
        onClick={() => {
          closeMobileActions()
          openStyleHistory()
        }}
        role="menuitem"
        type="button"
      >
        <History size={17} />
        <span>History</span>
      </button>
    )

    return () => setMobileMenuItems(null)
  }, [closeMobileActions, setMobileMenuItems])

  const copyText = async (value: string, label: string): Promise<void> => {
    await navigator.clipboard.writeText(value)
    toast(`Copied ${label}.`)
  }

  return (
    <>
      <ToolDetailHeader
        action={(
          <Button className={styles['tools-history-header-button']} onClick={openStyleHistory}>
            <History size={16} /> History
          </Button>
        )}
        subtitle="Turn free-form music intent into concise Suno-ready style variants."
        title="Style Prompt Generator"
      />

      <StylePromptWorkspace
        clearAvailable={styleClearAvailable}
        description={styleDescription}
        descriptionValid={styleDescriptionValid}
        error={styleError}
        generationMode={styleGenerationMode}
        isLoading={styleLoading}
        onClearBasePrompt={clearSelectedBasePrompt}
        onClearWorkspace={clearSunoStylePromptWorkspace}
        onCopyText={copyText}
        onDescriptionChange={setStyleDescription}
        onGenerationModeChange={setStyleGenerationMode}
        onOpenPromptLibrary={openPromptLibrary}
        onSaveSuggestion={openGeneratedPromptSaveDialog}
        onSubmit={submitSunoStylePrompts}
        selectedBasePrompt={selectedBasePrompt}
        suggestions={styleSuggestions}
      />

      <StylePromptOverlays
        historyDeletingId={styleHistoryDeletingId}
        historyError={styleHistoryError}
        historyItems={styleHistory}
        historyLoading={styleHistoryLoading}
        historyOpen={styleHistoryOpen}
        onCloseHistory={() => setStyleHistoryOpen(false)}
        onClosePromptEditor={() => setPromptEditor(null)}
        onClosePromptLibrary={() => setPromptLibraryOpen(false)}
        onDeleteHistoryItem={(item) => void deleteStyleHistoryItem(item)}
        onOpenHistoryItem={openStyleHistoryItem}
        onPromptEditorChange={setPromptEditor}
        onPromptLibrarySearch={(search) => void loadPromptLibrary(search)}
        onPromptLibrarySearchChange={setPromptLibrarySearch}
        onPromptLibrarySelect={selectPromptLibraryPrompt}
        onPromptSave={(input) => void savePrompt(input)}
        onSuggestPromptMetadata={suggestPromptMetadata}
        promptEditor={promptEditor}
        promptGroups={sunoPromptGroups}
        promptLibraryError={promptLibraryError}
        promptLibraryItems={promptLibraryPrompts}
        promptLibraryLoading={promptLibraryLoading}
        promptLibraryOpen={promptLibraryOpen}
        promptLibrarySearch={promptLibrarySearch}
        promptLibrarySelectedId={selectedBasePrompt?.id ?? null}
        promptSaving={promptSaving}
      />
    </>
  )
}
